/*
 * Authors: mariushirjanu, Triadi
 * Created: 12/06/2020
 * Upgraded: 06/12/2023
 */
import _ from 'underscore';
import moment from 'moment';
import { AssetColumn, AssetDataService, IAsset, IAssetInfoCalculation, IAssetResponse, ISOWResult } from './asset-data.service';
import { CellClassParams, CellValueChangedEvent, ColDef, Column, GridOptions, ICellRendererParams, IRowNode, RowNode, ValueFormatterParams } from 'ag-grid-community';
import { FormattingService } from '@app/shared/services/formatting.service';
import { IDescribedStringObject } from '@type_definitions/datatypes';
import { MaxWidth, ResponsiveService } from '@app/shared/responsive/responsive.service';
import { IColDef } from '@app/shared/report/report-grid.service';
import { Component, Inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormlyType, GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { GaMessageService } from '@app/shared/message/ga-message.service';
import { UntypedFormGroup } from '@angular/forms';
import { EVENT_ROW_DATA_CHANGED, IFormGridColDef } from '@app/shared/form-grid/form-grid.component';
import { CellRendererComp } from '@app/shared/ag-grid-components/ga-ag-grid.component';
import { IDetailCellRendererParamsExtended } from '@app/shared/ag-grid-components/ag-detail.component';
import { AssetDetailComponent } from './asset-detail.component';
import { GaEventService, IGAEvent } from '@app/shared/services/ga-event.service';
import { Subscription } from 'rxjs';
import { EVENT_CHECK_SOW, EVENT_SAVE_ORDER, GA1Service } from '@app/shared/ga1/ga1.service';
import { AgCellValidatorService, IAgCellValidator } from '@app/shared/ag-grid-components/ag-cell-validator/ag-cell-validator.service';
import { AG_CELL_VALIDATOR, AgCellValidatorComponent } from '@app/shared/ag-grid-components/ag-cell-validator/ag-cell-validator.component';
import { GaAgGridService } from '@app/shared/ag-grid-components/ga-ag-grid.service';
import { GRID_ORDER_ASSET } from '@app/shared/app-grids';

interface IAssetFilter {
  type?:IDescribedStringObject;
  serialisable?:boolean;
}

interface IGA1SaveOrderContext {
  isAssetValidationRequired?:boolean;
}

enum PaymentPeriod {
  M = 'Monthly',
  HY = 'Half-year',
  Y = 'Yearly',
  Q = 'Quarterly'
}

const CATEGORY_CAREPACKS:string = 'CAREPACKS';

@Component({
  selector: 'asset-info',
  template: `
    <div class="orderentry-asset col-space-top">
      <formly [form]="form" [fields]="filterFields" [model]="filter" [noValidate]="true" class="form-horizontal"></formly>
      <faq-popup class="text-primary" faqCode="SERIAL_REPORTING"><i class="fa fa-info-circle asset-icon-info"></i> <text key="FAQ_SERIAL_REPORTING"></text></faq-popup>
      <p *ngIf="!readonly" class="text-primary"><i class="fa fa-info-circle asset-icon-info"></i> <text key="CELL_INFO" screen="ORDERENTRY.ASSET"></text></p>
      <form-grid
        class="asset-grid"
        [gridId]="gridId"
        title="ORDERENTRY.ASSET.TITLE"
        [loading]="loading"
        [gridOptions]="gridOptions"
        [rowData]="rowData"
        [columnData]="columnData"
        [allowEdit]="!readonly"
        fillHeight="45px;.double-space-top;.form-horizontal;.ui-layout-north;.ui-layout-resizer-north"
        [stopEditing]="stopEditing"
      ></form-grid>
    </div>
  `,
  styleUrls: ['./asset.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AssetInfoComponent implements OnInit, OnDestroy {
  @Input() public orderId:number;
  @Input() public type:string;
  @Input() public serialisable:boolean;
  @Input() public readonly:boolean;

  public gridId:string = GRID_ORDER_ASSET;
  public gridOptions:GridOptions;
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public filterFields:IExtraFieldConfigurationObject[];
  public filter:IAssetFilter = {
    type: undefined,
    serialisable: true
  };
  private isDataChanged:boolean;
  private assetData:IAssetResponse;
  public loading:boolean;
  public rowData:IAsset[];
  public columnData:IColDef[];
  private eventHandler:Subscription;
  private ga1EventHandler:Subscription;
  public stopEditing:boolean;

  private readonly filterOptions:IDescribedStringObject[] = [ {
    id: 'G',
    description: 'ORDERENTRY.ASSET.RADIO_GENERAL.LABEL'
  }, {
    id: 'P',
    description: 'ORDERENTRY.ASSET.RADIO_PHYSICAL.LABEL'
  }, {
    id: 'W',
    description: 'ORDERENTRY.ASSET.RADIO_WARRANTY.LABEL'
  }, {
    id: 'L',
    description: 'ORDERENTRY.ASSET.RADIO_LEASING.LABEL'
  }, {
    id: 'S',
    description: 'ORDERENTRY.ASSET.RADIO_ITEMSTATUS.LABEL'
  } ];

  constructor(
    private assetDataService:AssetDataService,
    private agCellValidatorService:AgCellValidatorService,
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private translationService:TranslationService,
    private messageService:GaMessageService,
    @Inject(Window) private window:Window,
    private responsiveService:ResponsiveService,
    private eventService:GaEventService,
    private ga1Service:GA1Service,
    private gaAgGridService:GaAgGridService
  ) {
    // set window width to maximum
    responsiveService.width = MaxWidth.LG;

    // this event listener will be triggered when asset iframe in ga1 orderentry visibility change
    // When iframe still hidden (display: none), ag-grid determine grid width is zero so auto column will not work properly
    // When iframe becomes visible then notify ag-grid to recalculate columns width (autosize)
    this.window.addEventListener('resize', this.onResize, true);
  }

  public ngOnInit():void {
    this.filter.type = _.findWhere(this.filterOptions, { id: this.type || 'G' });

    // create filter fields
    this.filterFields = this.createFilterFields();

    this.installGrid();
    this.loadData();

    this.listen();
  }

  private listen():void {
    // add save event listener only when 'readonly' parameter is not defined or false
    if (!this.readonly) {
      this.eventHandler = this.eventService.events
        .subscribe((event:IGAEvent) => {
          if(event.name === EVENT_ROW_DATA_CHANGED){
            this.onRowChange(event.data);
          }
        });

      this.ga1EventHandler = this.ga1Service.events
        .subscribe((event:IGAEvent) => {
          switch (event.name) {
            case EVENT_SAVE_ORDER:
              this.onSaveOrder(event.data);
            break;

            // this is sent from GA1 for updating the SOW tab. In this tab, we show a separate validation result for CAREPACKs
            case EVENT_CHECK_SOW:
              this.onCheckSOW();
            break;
          }
        });
    }
  }

  private getVisibleColumns(typeId:string):ColDef[] {
    if (typeId) {
      const colDefs:ColDef[] = [];

      _.each(this.assetDataService.visibleColumns[ typeId ], (colKey:string) => {
        const colDef:IFormGridColDef = _.findWhere(this.colDefs, { field: colKey });

        if(colDef) {
          // show/hide ship fields for BP
          if (_.indexOf(this.assetDataService.shipToBPFields, colKey) !== -1) {
            colDef.hide = !this.assetData?.displayShipFieldForBP;
          }

          // show/hide ship fields for Distro
          if (_.indexOf(this.assetDataService.shipToDistriFields, colKey) !== -1) {
            colDef.hide = !this.assetData?.displayShipFieldForDistri;
          }

          colDefs.push(colDef);
        }
      });

      return colDefs;
    }
  }

  private get colDefs():IFormGridColDef[] {
    const colDefs:IFormGridColDef[] = [
      {
        field: AssetColumn.PART_NUMBER,
        headerName: 'ORDERENTRY.ASSET.PARTNUMBER.LABEL',
        cellRenderer: CellRendererComp.GROUP_CELL_RENDERER,
        pinned: 'left',
        resizable: true,
        minWidth: 100,
        tooltipField: '',
        editable: false
      },
      {
        field: AssetColumn.CATEGORY,
        headerName: 'ORDERENTRY.ASSET.CATEGORY.LABEL',
        resizable: true,
        minWidth: 100
      },
      {
        field: AssetColumn.DESCRIPTION,
        headerName: 'ORDERENTRY.ASSET.DESCRIPTION.LABEL',
        resizable: true,
        minWidth: 150,
        flex: 1
      },
      {
        field: AssetColumn.SERIAL_NUMBER,
        headerName: 'ORDERENTRY.ASSET.SERIALNUMBER.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        }
      },
      {
        field: AssetColumn.ASSET_TAG,
        headerName: 'ORDERENTRY.ASSET.ASSETTAG.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        }
      },
      {
        field: AssetColumn.ASSET_DESCRIPTION,
        headerName: 'ORDERENTRY.ASSET.ASSETDESCRIPTION.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        }
      },
      {
        field: AssetColumn.USER,
        headerName: 'ORDERENTRY.ASSET.USER.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        }
      },
      {
        field: AssetColumn.MAC_ADDRESS,
        editable: !this.readonly,
        headerName: 'ORDERENTRY.ASSET.MACADDRESS.LABEL',
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        }
      },
      {
        field: AssetColumn.MAC2_ADDRESS,
        editable: !this.readonly,
        headerName: 'ORDERENTRY.ASSET.MAC2ADDRESS.LABEL',
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        }
      },
      {
        field: AssetColumn.IMEI,
        headerName: 'ORDERENTRY.ASSET.IMEI.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        }
      },
      {
        field: AssetColumn.UUID,
        headerName: 'ORDERENTRY.ASSET.UUID.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        }
      },
      {
        field: AssetColumn.PARENT_PRODUCT_SERIAL_NUMBER,
        headerName: 'ORDERENTRY.ASSET.PARENTPRODUCTSERIALNUMBER.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        }
      },
      {
        field: AssetColumn.WARRANTY_EXPIRY_DATE,
        headerName: 'ORDERENTRY.ASSET.WARRANTYEXPIRYDATE.LABEL',
        editable: !this.readonly,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.WARRANTY_EXPIRY_DATE)
        },
        valueFormatter: this.dateValueFormatter,
        resizable: true,
        minWidth: 100
      },
      {
        field: AssetColumn.WARRANTY_ACTIVATION_NUMBER,
        headerName: 'ORDERENTRY.ASSET.WARRANTYACTIVATIONNUMBER.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        }
      },
      {
        field: AssetColumn.LEASING_CONTRACT_NUMBER,
        headerName: 'ORDERENTRY.ASSET.LEASINGCONTRACTNUMBER.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        }
      },
      {
        field: AssetColumn.LEASING_START_DATE,
        headerName: 'ORDERENTRY.ASSET.LEASINGSTARTDATE.LABEL',
        editable: !this.readonly,
        valueFormatter: this.dateValueFormatter,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.LEASING_START_DATE)
        }
      },
      {
        field: AssetColumn.LEASING_TERM,
        headerName: 'ORDERENTRY.ASSET.LEASINGTERM.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        }
      },
      {
        field: AssetColumn.RENTAL_EXTENSION,
        headerName: 'ORDERENTRY.ASSET.RENTALEXTENSION.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        }
      },
      {
        field: AssetColumn.LEASING_END_DATE,
        headerName: 'ORDERENTRY.ASSET.LEASINGENDDATE.LABEL',
        valueFormatter: this.dateValueFormatter,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.LEASING_END_DATE)
        }
      },
      {
        field: AssetColumn.RENTAL_END_DATE,
        headerName: 'ORDERENTRY.ASSET.RENTALENDDATE.LABEL',
        valueFormatter: this.dateValueFormatter,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.RENTAL_END_DATE)
        }
      },
      {
        field: AssetColumn.PAYMENT_PERIOD,
        headerName: 'ORDERENTRY.ASSET.PAYMENTPERIOD.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.SELECT,
          options: [
            { id: PaymentPeriod.M },
            { id: PaymentPeriod.Q },
            { id: PaymentPeriod.HY },
            { id: PaymentPeriod.Y }
          ],
          valueProp: 'id',
          labelProp: 'id',
          format: this.formattingService.formatLov
        }
      },
      {
        field: AssetColumn.BEGINNING_OF_PAYMENT,
        headerName: 'ORDERENTRY.ASSET.BEGINNINGOFPAYMENT.LABEL',
        editable: !this.readonly,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.BEGINNING_OF_PAYMENT)
        },
        valueFormatter: this.dateValueFormatter,
        resizable: true,
        minWidth: 100
      },
      {
        field: AssetColumn.END_OF_PAYMENT,
        headerName: 'ORDERENTRY.ASSET.ENDOFPAYMENT.LABEL',
        valueFormatter: this.dateValueFormatter,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.END_OF_PAYMENT)
        },
        resizable: true,
        minWidth: 100
      },
      {
        field: AssetColumn.OEM_REFERENCE_NUMBER,
        headerName: 'ORDERENTRY.ASSET.OEMREFERENCENUMBER.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        }
      },
      {
        field: AssetColumn.ESTIMATE_SHIP_DATE_TO_CUSTO,
        headerName: 'ORDERENTRY.ASSET.SHIPPINGESTIMATIONDATE.LABEL',
        editable: !this.readonly,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.ESTIMATE_SHIP_DATE_TO_CUSTO)
        },
        valueFormatter: this.dateValueFormatter,
        resizable: true,
        minWidth: 100,
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.SHIPPINGESTIMATIONDATE_HOVER')
      },
      {
        field: AssetColumn.ACTUAL_SHIP_DATE_TO_CUSTO,
        headerName: 'ORDERENTRY.ASSET.SHIPPINGACTUALDATE.LABEL',
        editable: !this.readonly,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.ACTUAL_SHIP_DATE_TO_CUSTO)
        },
        valueFormatter: this.dateValueFormatter,
        resizable: true,
        minWidth: 100,
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.SHIPPINGACTUALDATE_HOVER')
      },
      {
        field: AssetColumn.ESTIMATE_DELIVER_DATE_AT_CUSTO,
        headerName: 'ORDERENTRY.ASSET.ARRIVALESTIMATIONDATE.LABEL',
        editable: !this.readonly,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.ESTIMATE_DELIVER_DATE_AT_CUSTO)
        },
        valueFormatter: this.dateValueFormatter,
        resizable: true,
        minWidth: 100,
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.ARRIVALESTIMATIONDATE_HOVER')
      },
      {
        field: AssetColumn.ACTUAL_DELIVER_DATE_AT_CUSTO,
        headerName: 'ORDERENTRY.ASSET.ARRIVALACTUALDATE.LABEL',
        editable: !this.readonly,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.ACTUAL_DELIVER_DATE_AT_CUSTO)
        },
        valueFormatter: this.dateValueFormatter,
        resizable: true,
        minWidth: 100,
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.ARRIVALACTUALDATE_HOVER')
      },
      {
        field: AssetColumn.TRACKING_NUMBER_TO_CUSTO,
        headerName: 'ORDERENTRY.ASSET.TRACKINGCUSTOMER.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        },
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.TRACKINGCUSTOMER_HOVER')
      },
      {
        field: AssetColumn.ESTIMATE_SHIP_DATE_TO_BP,
        headerName: 'ORDERENTRY.ASSET.ETSBP.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 130,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.ESTIMATE_SHIP_DATE_TO_BP)
        },
        valueFormatter: this.dateValueFormatter,
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.ETSBP_HOVER'),
        flex: 1
      },
      {
        field: AssetColumn.ACTUAL_SHIP_DATE_TO_BP,
        headerName: 'ORDERENTRY.ASSET.ATSBP.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 130,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.ACTUAL_SHIP_DATE_TO_BP)
        },
        valueFormatter: this.dateValueFormatter,
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.ATSBP_HOVER'),
        flex: 1
      },
      {
        field: AssetColumn.ESTIMATE_DELIVER_DATE_AT_BP,
        headerName: 'ORDERENTRY.ASSET.ETABP.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 130,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.ESTIMATE_DELIVER_DATE_AT_BP)
        },
        valueFormatter: this.dateValueFormatter,
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.ETABP_HOVER'),
        flex: 1
      },
      {
        field: AssetColumn.ACTUAL_DELIVER_DATE_AT_BP,
        headerName: 'ORDERENTRY.ASSET.ATABP.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 130,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.ACTUAL_DELIVER_DATE_AT_BP)
        },
        valueFormatter: this.dateValueFormatter,
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.ATABP_HOVER'),
        flex: 1
      },
      {
        field: AssetColumn.TRACKING_NUMBER_TO_BP,
        headerName: 'ORDERENTRY.ASSET.TRACKINGBP.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 130,
        cellEditorParams: {
          type: FormlyType.INPUT
        },
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.TRACKINGBP_HOVER')
      },
      {
        field: AssetColumn.ESTIMATE_SHIP_DATE_TO_DISTRI,
        headerName: 'ORDERENTRY.ASSET.ETSDISTRIBUTOR.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.ESTIMATE_SHIP_DATE_TO_DISTRI)
        },
        valueFormatter: this.dateValueFormatter,
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.ETSDISTRIBUTOR_HOVER')
      },
      {
        field: AssetColumn.ACTUAL_SHIP_DATE_TO_DISTRI,
        headerName: 'ORDERENTRY.ASSET.ATSDISTRIBUTOR.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.ACTUAL_SHIP_DATE_TO_DISTRI)
        },
        valueFormatter: this.dateValueFormatter,
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.ATSDISTRIBUTOR_HOVER')
      },
      {
        field: AssetColumn.ESTIMATE_DELIVER_DATE_AT_DISTRI,
        headerName: 'ORDERENTRY.ASSET.ETADISTRIBUTOR.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.ESTIMATE_DELIVER_DATE_AT_DISTRI)
        },
        valueFormatter: this.dateValueFormatter,
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.ETADISTRIBUTOR_HOVER')
      },
      {
        field: AssetColumn.ACTUAL_DELIVER_DATE_AT_DISTRI,
        headerName: 'ORDERENTRY.ASSET.ATADISTRIBUTOR.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT,
          minDate: this.getMinDate(),
          maxDate: this.getMaxDate(AssetColumn.ACTUAL_DELIVER_DATE_AT_DISTRI)
        },
        valueFormatter: this.dateValueFormatter,
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.ATADISTRIBUTOR_HOVER')
      },
      {
        field: AssetColumn.TRACKING_NUMBER_TO_DISTRI,
        headerName: 'ORDERENTRY.ASSET.TRACKINGDISTRIBUTOR.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        },
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.TRACKINGDISTRIBUTOR_HOVER')
      },
      {
        field: AssetColumn.WAN_IMEI,
        headerName: 'ORDERENTRY.ASSET.WANIMEI.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        },
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.WANIMEI.LABEL')
      },
      {
        field: AssetColumn.UUID_HR,
        headerName: 'ORDERENTRY.ASSET.UUIDHR.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        },
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.UUIDHR.LABEL')
      },
      {
        field: AssetColumn.WLAN_MAC,
        headerName: 'ORDERENTRY.ASSET.WLANMAC.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        },
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.WLANMAC.LABEL')
      },
      {
        field: AssetColumn.WARRANTY_START_DATE,
        headerName: 'ORDERENTRY.ASSET.WARRANTYSTARTDATE.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.DATE_INPUT
        },
        valueFormatter: this.dateValueFormatter,
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.WARRANTYSTARTDATE.LABEL')
      },
      {
        field: AssetColumn.FLASH_SYSBIOS_LEVEL,
        headerName: 'ORDERENTRY.ASSET.FLASHSYSBIOSLEVEL.LABEL',
        editable: !this.readonly,
        resizable: true,
        minWidth: 100,
        cellEditorParams: {
          type: FormlyType.INPUT
        },
        headerTooltip: this.translationService.instant('ORDERENTRY.ASSET.FLASHSYSBIOSLEVEL_HOVER')
      }
    ];

    return _.map(colDefs, (colDef:IFormGridColDef) => {
      colDef.cellEditor = CellRendererComp.CELL_EDITOR;
      colDef.cellRenderer = colDef.cellRenderer || AG_CELL_VALIDATOR; // apply 'agCellValidator' to columns in order to display validation errors
      colDef.tooltipField = ''; // hide default ag-grid tooltip (instead is being used the popover defined in AG_CELL_VALIDATOR)

      if(colDef.editable) {
        // apply css classes to the cells
        colDef.cellClassRules = {
          // apply 'cell-error' css class when there is an error message -> this adds to the cell: red border, red color, ...
          'cell-error': (params:CellClassParams):boolean => !!this.agCellValidatorService.validateCell(params).length
        };
      }

      return colDef;
    });
  }

  private installGrid():void {
    this.gridOptions = {
      columnDefs: this.getVisibleColumns(this.filter?.type?.id),
      masterDetail: true,
      detailRowHeight: 200,
      detailCellRenderer: CellRendererComp.DETAIL,
      detailCellRendererParams: <IDetailCellRendererParamsExtended>{
        refreshStrategy: 'everything',
        component: AssetDetailComponent
      },
      components: {
        [AG_CELL_VALIDATOR]: AgCellValidatorComponent // display assets validation errors
      },
      onFilterChanged: this.gridAutoSizeColumns,
      stopEditingWhenCellsLoseFocus: false, // when set to false -> stop editing is done manually because the input field needs time to focus out before stopping editing

      isExternalFilterPresent: () => true,
      doesExternalFilterPass: (node:RowNode):boolean => this.filter.serialisable? (<IAsset>node.data).serialisable : true,
      context: {
        // configuration that is used in cellClassRules and cellRenderer for the field validation (this.agCellValidatorService.validate())
        // cellClassRules adds a css class to the column and cellRenderer adds an icon before the value
        validator: {
          serialNumber: {
            validateConfigFn: (params:ICellRendererParams):IAgCellValidator => {
              return {
                required: params.data.serialNumberRequired,
                minLength: params.data.serialNumberMinLength,
                maxLength: params.data.serialNumberMaxLength,
                pattern: params.data.vendorRegexPattern
              };
            }
          }
        }
      }
    };
  }

  private getRowsData():IAsset[] {
    const results:IAsset[] = [];

    if (this.gridOptions) {
      this.gaAgGridService.forEachNode(this.gridId, (node:RowNode) => {
        results.push(node.data);
      });
    }

    return results;
  }

  private loadData():void {
    this.loading = true;
    this.assetDataService.getAssetInfo(this.orderId)
      .then((assetData:IAssetResponse) => {
        this.assetData = _.clone(assetData);
        this.rowData = this.assetData?.assets;
        this.onFilterTypeChange();
      })
      .finally(() => {
        if(!this.rowData){
          this.rowData = []; // when the promise is rejected due to an error or 'assets' is undefined, set 'rowData' to an empty array ([]); this ensures that 'form-grid' detects changes and notifies ag-Grid to complete loading, displaying 'no results'
        }
        this.loading = false;
      });
  }

  private getMinDate():Date {
    return this.assetData?.orderDate && moment(this.assetData.orderDate).toDate();
  }

  private getMaxDate(field:AssetColumn):Date {
    return field.match(/actual/gi)? new Date() : undefined;
  }

  private onResize = (e:Event) => {
    // autosize all grid columns
    this.gridAutoSizeColumns();

    // remove event listener after first callback
    this.window.removeEventListener(e.type, this.onResize, true);
  };

  private gridAutoSizeColumns = ():void => {
    // resize all columns to content size except description column
    const columns:Column[] = [];

    _.each(this.gaAgGridService.getColumns(this.gridId), (column:Column) => {
      if (!column.getColDef()?.flex) {
        columns.push(column);
      }
    });
    // improve performance rather than doing it one-by-one
    this.gaAgGridService.autoSizeColumns(this.gridId, columns);
  };
  
  private validateFields(shouldValidateSerialNumberRequired:boolean, showErrorDialog:boolean):Promise<string | void> {
    let errors:string[] = [];

    for (let idx:number = 0; idx < this.gaAgGridService.getDisplayedRowCount(this.gridId); idx++) {
      const rowNode:IRowNode = this.gaAgGridService.getDisplayedRowAtIndex(this.gridId, idx);
      const data:IAsset = _.clone(rowNode.data);

      let keys:AssetColumn[] = <AssetColumn[]>Object.getOwnPropertyNames(data);
      keys.push(AssetColumn.SERIAL_NUMBER); // make sure serialNumber is always checked, also when it's not present on the object
      keys = _.uniq(keys);

      // loop here every object property on each indexes then do validation
      // colKey contains ag-grid field name (cell name)
      for (const colKey of keys) {
        let errorMessages:string[] = [];
        const column:Column = this.gaAgGridService.getColumn(this.gridId, colKey);
        if (column) { // make sure that the column is a valid column
          const colDef:IFormGridColDef = column.getColDef();

          errorMessages = this.agCellValidatorService.validateCell({
            colDef,
            context: this.gridOptions.context,
            data,
            value: data[colDef.field]
          }, {
            // Serial number validation for the 'required' condition is applied only when 'shouldValidateSerialNumberRequired' is set to true
            validateRequired: colDef.field === AssetColumn.SERIAL_NUMBER && shouldValidateSerialNumberRequired,
            validateRequiredUndefined: true
          });

          // this object will be used to display error using messageService.showErrorDialog with uniq messages
          errors = _.uniq(errors.concat(errorMessages));
        }
      }
    }

    // If there are local errors means we reject with showing error dialog with list of error messages
    if (errors.length) {
      if (showErrorDialog) {
        this.messageService.showErrorDialog(this.translationService.instant('GA.VALIDATION.VALIDATION_FAILED'), `
				<ul>
					${_.map(errors, (error:string) => {
          return `<li>${error}</li>`;
        }).join('')}
				</ul>
			`);
      }

      // check for any validation errors associated with the serial number
      const isSerialNumberValid:number = _.findIndex(errors, (error:string) => error.includes(this.translationService.instant('GA.VALIDATION.REQUIRED', { label: this.translationService.instant('ORDERENTRY.ASSET.SERIALNUMBER.LABEL') } )));

      return Promise.reject(shouldValidateSerialNumberRequired && isSerialNumberValid !== -1 ? 'ORDERENTRY.ASSET.VALIDATION.SERIALNR_REQUIRED' : undefined);
    }

    return Promise.resolve();
  }

  private getCalculatedFields(event:CellValueChangedEvent):void {
    const calculatedFields:string[] = [
      AssetColumn.LEASING_START_DATE,
      AssetColumn.LEASING_TERM,
      AssetColumn.RENTAL_EXTENSION
    ];

    if (calculatedFields.indexOf(event.colDef.field) > -1) {
      const requestData:IAssetInfoCalculation = {
        assetId: event.node.data.id,
        leasingStartDate: event.node.data.leasingStartDate,
        leasingTerm: event.node.data.leasingTerm,
        rentalExtension: event.node.data.rentalExtension
      };

      if (!requestData.leasingStartDate) {
        // if leasing start date is empty then clear leasingEndDate and rentalEndDate
        // also don't request to calculate fields rest api
        event.node.setData(_.extend(event.node.data, {
          leasingEndDate: undefined,
          rentalEndDate: undefined
        }));
      } else {
        this.assetDataService.calculateFields(this.orderId, requestData)
          .then((data:IAssetInfoCalculation) => {
            // update row data
            event.node.setData(_.extend(event.node.data, data));
          });
      }
    }
  }

  private dateValueFormatter = (params:ValueFormatterParams):string => {
    return this.formattingService.formatDate(params.value);
  };

  private onFilterTypeChange = ():void => {
    const typeId:string = this.filter.type?.id;
    
    if (typeId && this.gridOptions) {
      // uncheck serializable when tab change to 'item status'
      this.filter.serialisable = typeId !== 'S';
      this.columnData = this.getVisibleColumns(typeId);
      this.gaAgGridService.onFilterChanged(this.gridId);
    }
  };

  private createFilterFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aRadio('type', undefined)
        .withOptions(this.filterOptions)
        .withFormat(this.formattingService.formatLov)
        .withoutWrapper()
        .withInline()
        .onChange(this.onFilterTypeChange)
        .build(),

      this.formlyService.aCheckbox('serialisable')
        .withoutWrapper()
        .withWrapperClass('group')
        .onChange(() => this.gaAgGridService.onFilterChanged(this.gridId))
        .build()
    ];
  }

  private onRowChange(event:CellValueChangedEvent):void {
    this.isDataChanged = true;
    this.getCalculatedFields(event);
  }

  // ga1 event onSaveOrder
  private onSaveOrder(data:IGA1SaveOrderContext):void {
    // before performing validation -> exit ag-grid editing mode to apply validation to the cell
    this.stopEditing = !this.stopEditing;

    // wait little bit to allow ag-grid to conclude editing before initiating validation, a timeout higher than 0 is also important because EVENT_ROW_DATA_CHANGED even has to be received before performing the validation
    setTimeout(() => {
      this.validateFields(data?.isAssetValidationRequired, true)
        .then(() => {
          if (!this.isDataChanged) {
            this.assetDataService.notifyAssetSaved();
          } else {
            // notify ga1 asset is currently saving
            this.assetDataService.notifyAssetSaving();

            this.assetDataService.saveAssetInfo(this.orderId, this.getRowsData())
              .then(this.assetDataService.notifyAssetSaved)
              .catch(this.assetDataService.notifyAssetSaveFailed);
          }
          this.isDataChanged = undefined;
        })
        .catch((error:string) => {
          if (error) {
            this.messageService.showErrorMessage(this.translationService.instant(error));
          }

          this.assetDataService.notifyAssetValidationFailed();
        });
    }, 50);
  }

  // ga1 event onCheckSOW
  private onCheckSOW():void {
    this.validateFields(true, false)
      .then(() => {
        this.assetDataService.notifySOWResult({
          SERIAL: true,
          CAREPACK: true
        });
      })
      .catch(() => {
        const result:ISOWResult = {
          SERIAL: false,
          CAREPACK: true
        };
        for (const asset of this.getRowsData()) {
          if (!asset.serialNumber && asset.category === CATEGORY_CAREPACKS) {
            result.CAREPACK = false;
            break;
          }
        }
        this.assetDataService.notifySOWResult(result);
      });
  }

  public ngOnDestroy():void {
    this.eventHandler?.unsubscribe();
    this.ga1EventHandler?.unsubscribe();
    this.responsiveService.width = MaxWidth.SM; // revert width
  }
}